import get from 'lodash/get';
import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import localforage from 'localforage';
import NRouter, { Router } from '../router';
import * as actions from './actions';
import { fetchFeaturedAdRequest, resetFeaturedAd } from '../FeaturedAd/actions';
import { changeAdPage, fetchRelatedAdsRequest } from '../AdPage/actions';
import { saveScrollLatestPosition } from '../UI/scroll/actions';
import { trackEvent } from '../tracking/actions';
import {
  clickoutEvent,
  clickinEvent,
  clickinFormEvent,
  adPageClickoutEvent,
} from '../tracking/events';
import {
  SET_SEARCH_PARAMS,
  CLICKOUT_REQUEST,
  FETCH_AUTOCOMPLETE_REQUEST,
  FETCH_ADS_VISIT_COUNTER,
  FETCH_ADS_SUCCESS,
  SET_OR_INCREMENT_AD_VISIT_COUNTER,
  LOCAL_FORAGE_KEYS,
  REDIRECT_TO_AD_SOURCE,
  REDIRECT_TO_AD_PAGE,
  SHOW_RELATED_ADS_POPUP,
  HIDE_RELATED_ADS_POPUP,
  SECTIONS_WITHOUT_MODAL,
  REDIRECT_URL_PARAMETERS,
  SET_SEARCH_PARAMS_PAGE,
} from './constants';
import clientConfig from '../../parameters/client/config';
import SearchApi from '../sdk/search/Api';
import ClickoutApi from '../sdk/clickout/Api';
import ApiSearchError from '../sdk/search/ApiSearchError';
import { infrastructureIdSelector } from '../Notifications/selectors';
import {
  shouldResetWhereSelector,
  searchParametersSelector,
  isUserSearchInteractionSelector,
  getAdsVisitCounter,
  getRedirectData,
  getIsSeoUrl,
  isUserFilterInteractionSelector,
  isSemUrlSelector,
} from './selectors';
import normalizer from '../utils/adNormalizers/normalizer';
import requestMapper from './mappers/apiSearchRequest/requestMapper';
import responseMapper from './mappers/apiSearchResponse/responseMapper';
import {
  tranformSearchParamsToQuerySeoParams,
  transformSearchParametersToQueryObject,
} from '../utils/searchParameters';
import { shouldRedirectingLayoutShow as shouldRedirectingLayoutShowSelector } from '../Sources/redirectingTo/selectors';
import { showRedirectingTo } from '../Sources/redirectingTo/actions';
import { loadCountFromStorage } from '../Sources/redirectingTo/sagas';
import { urlHasParameter } from '../utils/strings';
import {
  getMinimumCharactersLengthByCountryCode,
  atLeastOneInputIsValid,
} from '../utils/validation';
import { handleSagaErrors } from '../Error/sagas';
import { VERTICAL_NAMES } from '../common/constants/common';
import { configSelector, getVerticalName, getCountryCode } from '~/common/selectors/config';
import { trovitSelector } from '~/common/selectors/trovit';

const searchApi = new SearchApi(clientConfig.searchApi);
const clickoutApi = new ClickoutApi(clientConfig.clickoutApi);

export function* requestAds(searchParams) {
  try {
    let responseData = {};
    const isSemUrl = get(searchParams, 'isSemUrl');
    const boostAdsSince = get(searchParams, 'boost_ads_since');

    const config = yield select(configSelector);
    const trovit = yield select(trovitSelector);
    const userId = yield select(infrastructureIdSelector);
    const shouldResetWhere = yield select(shouldResetWhereSelector);

    const verticalName = config.verticalName;
    const requestData = {
      searchParams,
      trovit,
      userId,
    };
    const requestApiParams = requestMapper(verticalName, requestData, shouldResetWhere);

    yield put(actions.fetchAdsStart());

    // Test AB for task PWA-1095 - More like this (Featured ad on top)
    yield put(resetFeaturedAd());

    if (isSemUrl) {
      const apiResponse = yield call([searchApi, searchApi.searchLandingAds], requestApiParams);
      responseData = yield call([normalizer, normalizer.normalizeLandingAds], apiResponse);
    } else if (boostAdsSince) {
      // Test AB for task PWA-1095 - More like this (Featured ad on top)
      // We request for a featured ad if a featured id param is on the request
      const featuredAdId = get(trovit, ['query', 'featured']);
      const isExpiredAd = get(trovit, ['query', 'expired']);
      const shouldFetchFeaturedAd =
        !isExpiredAd &&
        featuredAdId &&
        config.verticalName !== VERTICAL_NAMES.jobs &&
        config.verticalName !== VERTICAL_NAMES.products;
      if (shouldFetchFeaturedAd) {
        yield put(fetchFeaturedAdRequest(featuredAdId));
        requestApiParams.queryParams.excludedAdIds = featuredAdId;
      }
      // End test AB

      const apiResponse = yield call([searchApi, searchApi.searchRetentionAds], requestApiParams);
      responseData = yield call([normalizer, normalizer.normalizeRetentionAds], apiResponse);
    } else {
      const apiResponse = yield call([searchApi, searchApi.searchAds], requestApiParams);
      responseData = yield call([normalizer, normalizer.normalizeSearchAds], apiResponse);
    }

    const payload = {
      verticalName,
      responseData,
    };

    const responseMapped = responseMapper(payload);

    responseMapped.serp = responseData.serp;
    responseMapped.data = responseData.data;
    responseMapped.suggestions = responseData.suggestions;

    const { totalCount, totalPremiumCount } = responseMapped.serp.meta;
    let count = totalCount;

    if (isSemUrl && totalPremiumCount > 0) {
      count += totalPremiumCount;
    }

    if (count === 0) {
      yield put(actions.fetchAdsCompleted(responseMapped));
      throw new ApiSearchError(404, 'No results for this query', 404);
    }

    yield put(actions.fetchAdsSuccess(responseMapped));
  } catch (error) {
    yield call(handleSagaErrors, error.code || error.status);
    yield put(actions.fetchAdsFailed(error));
  }
}

export function* completeActionDataWithStateInfo(action) {
  const { data } = action;
  const config = yield select(configSelector);
  const trovit = yield select(trovitSelector);

  const origin = trovit.origin;
  const userCampaignIds = trovit.userCampaignIds;

  const sections = get(config, ['vertical', 'adsPerSections']);

  return {
    ...data,
    country_code: config.countryCode,
    vertical_name: config.verticalName,
    sections,
    origin,
    userCampaignIds,
  };
}

export function mustRetrieveKeywords(isSeoUrl) {
  if (isSeoUrl) {
    return true;
  }

  return false;
}

export function* setSearchParams(action) {
  const searchParameters = yield call(completeActionDataWithStateInfo, action);
  const isSeoUrl = get(searchParameters, 'isSeoUrl');
  const retrieveKeywords = mustRetrieveKeywords(isSeoUrl);
  const searchParametersFromState = yield select(searchParametersSelector);
  const config = yield select(configSelector);
  const countryCode = config.countryCode;

  const updatedSearchParametersCompleted = {
    ...searchParameters,
    geoId: get(searchParametersFromState, 'geoId'),
    type_id: get(searchParametersFromState, 'type_id'),
    isUserSearchInteraction: get(searchParametersFromState, 'isUserSearchInteraction'),
    retrieveKeywords,
  };

  const inputs = {
    what: get(searchParameters, 'what_d'),
    where: get(searchParameters, 'where'),
  };

  if (atLeastOneInputIsValid(inputs, getMinimumCharactersLengthByCountryCode(countryCode))) {
    yield call(requestAds, updatedSearchParametersCompleted);
  }
}

export function* clickoutRequest({ payload }) {
  const { sectionId, sectionType, forceNoAdpage, forceAdpage, ad } = payload;

  const redirectData = yield select(getRedirectData, ad, sectionId, sectionType, forceNoAdpage);

  if (ad.redirectUrl && !forceAdpage) {
    const userId = yield select(infrastructureIdSelector);
    let redirectUrl = ad.redirectUrl;

    if (userId && !urlHasParameter(redirectUrl, REDIRECT_URL_PARAMETERS.userId)) {
      redirectUrl += `${REDIRECT_URL_PARAMETERS.userId}.${userId}/`;
    }

    yield put(actions.redirectToAdSource(ad, redirectUrl));
    return;
  }

  try {
    yield put(actions.clickoutStart());
    const response = yield call([clickoutApi, clickoutApi.send], redirectData);
    const destination = response.data.meta.destination;
    const conversionCookie = response.data.meta.user && response.data.meta.user.conversion_cookie;
    const destinationURL = destination.type === 1 && destination.url;

    if (destinationURL && !forceAdpage) {
      const redirectUrl = conversionCookie
        ? `${
            clientConfig.redirectApi
          }/index.php?cod=only_redirect&url=${destinationURL}&cookie=${conversionCookie}`
        : decodeURIComponent(destinationURL);

      // Track clickout or adpage_clickout base on the is_force_no_adpage param (true only on adpage)
      if (redirectData.is_force_no_adpage) {
        yield put(trackEvent(adPageClickoutEvent(destinationURL)));
      } else {
        yield put(trackEvent(clickoutEvent(destinationURL)));
      }

      yield put(actions.redirectToAdSource(ad, redirectUrl));
    } else {
      const adId = ad.id;
      if (get(ad, ['source', 'ad', 'hasAdpageForm'])) {
        yield put(trackEvent(clickinFormEvent(adId)));
      } else {
        yield put(trackEvent(clickinEvent(adId)));
      }

      yield put(changeAdPage(ad));
      yield put(actions.redirectToAdPage(ad));
    }

    yield put(actions.clickoutSuccess());
  } catch (error) {
    yield put(actions.clickoutFailed(error));
  }
}

export function* fetchAutocompleteRequest(action) {
  const { search } = action.data;
  const config = yield select(configSelector);
  const countryCode = config.countryCode;
  const verticalName = config.verticalName;

  try {
    yield put(actions.fetchAutocompleteStart());
    const response = yield call([searchApi, searchApi.autocomplete], {
      search,
      country_code: countryCode,
      vertical_name: verticalName,
    });

    const data = response.length ? response : [];

    yield put(actions.fetchAutocompleteSuccess(data));
  } catch (error) {
    yield put(actions.fetchAutocompleteFailed(error));
  }
}

function* updateUrl() {
  const searchParameters = yield select(searchParametersSelector);
  const verticalName = yield select(getVerticalName);
  const isUserSearchInteraction = yield select(isUserSearchInteractionSelector);
  const isUserFilterInteraction = yield select(isUserFilterInteractionSelector);
  const isSeoUrl = yield select(getIsSeoUrl);
  const isSemUrl = yield select(isSemUrlSelector);

  if (isUserFilterInteraction || isUserSearchInteraction) {
    if (isSeoUrl) {
      const { routeName, ...paramsWithoutRouteName } = tranformSearchParamsToQuerySeoParams(
        searchParameters
      );

      if (isSemUrl) {
        paramsWithoutRouteName.tg = this.props.query.tg;
      }

      return Router.pushRoute(routeName, paramsWithoutRouteName).then(() => window.scrollTo(0, 0));
    }

    const query = transformSearchParametersToQueryObject(searchParameters, verticalName);

    return Router.pushRoute('serp_search', query).then(() => window.scrollTo(0, 0));
  }

  return false;
}

function* setAdsVisitCounterToLocalStorage() {
  if (!navigator.cookieEnabled) {
    return;
  }

  try {
    const adsVisitCounter = yield select(getAdsVisitCounter);

    localforage.setItem(LOCAL_FORAGE_KEYS.adsVisitCounter, JSON.stringify(adsVisitCounter));
  } catch (err) {
    console.error(err); // eslint-disable-line no-console
  }
}

function* fetchAdsVisitCounter() {
  if (!navigator.cookieEnabled) {
    return;
  }

  try {
    const adsVisitCounter = yield call(
      [localforage, localforage.getItem],
      LOCAL_FORAGE_KEYS.adsVisitCounter
    );

    yield put(actions.setAdsVisitCounter(JSON.parse(adsVisitCounter)));
  } catch (err) {
    console.error(err); // eslint-disable-line no-console
  }
}

export function* redirectToAdSource({ payload: { redirectUrl, ad } }) {
  // TODO: find an async solution
  yield call(loadCountFromStorage);
  const shouldRedirectingLayoutShow = yield select(shouldRedirectingLayoutShowSelector);

  if (ad.visitCount === 1 && !SECTIONS_WITHOUT_MODAL.includes(ad.section_id)) {
    return yield put(actions.showRelatedAdsPopup(ad, redirectUrl));
  }

  if (shouldRedirectingLayoutShow) {
    return yield put(showRedirectingTo(redirectUrl));
  }

  const { scrollY, scrollX } = window;

  yield put(saveScrollLatestPosition(scrollX, scrollY));
  window.location = redirectUrl;
  return false;
}

export function* redirectToAdPage({ payload }) {
  if (payload.visitCount === 1 && !SECTIONS_WITHOUT_MODAL.includes(payload.section_id)) {
    const route = NRouter.findAndGetUrls('adpage', { id: payload.id });
    return yield put(actions.showRelatedAdsPopup(payload, `${window.origin}${route.urls.as}`));
  }

  const { scrollY, scrollX } = window;

  yield put(saveScrollLatestPosition(scrollX, scrollY));
  Router.pushRoute('adpage', { id: payload.id }).then(() => window.scrollTo(0, 0));
  return false;
}

function* showRelatedAdsPopup({ payload: { ad } }) {
  const verticalName = yield select(getVerticalName);
  const countryCode = yield select(getCountryCode);

  document.body.style.overflow = 'hidden';
  yield put(fetchRelatedAdsRequest({ ad, verticalName, countryCode, isFromRelatedModal: true }));
}

function hideRelatedAdsPopup() {
  document.body.style.overflow = '';
}

export function* setSearchParamsPage() {
  const searchParameters = yield select(searchParametersSelector);
  yield put(actions.setSearchParams(searchParameters));
}

export default function* serpSagas() {
  yield all([
    takeLatest(SET_SEARCH_PARAMS, setSearchParams),
    takeLatest(CLICKOUT_REQUEST, clickoutRequest),
    takeLatest(FETCH_AUTOCOMPLETE_REQUEST, fetchAutocompleteRequest),
    takeLatest(SET_OR_INCREMENT_AD_VISIT_COUNTER, setAdsVisitCounterToLocalStorage),
    takeLatest(FETCH_ADS_VISIT_COUNTER, fetchAdsVisitCounter),
    takeLatest(REDIRECT_TO_AD_SOURCE, redirectToAdSource),
    takeLatest(REDIRECT_TO_AD_PAGE, redirectToAdPage),
    takeLatest(SHOW_RELATED_ADS_POPUP, showRelatedAdsPopup),
    takeLatest(HIDE_RELATED_ADS_POPUP, hideRelatedAdsPopup),
    takeLatest(FETCH_ADS_SUCCESS, updateUrl),
    takeLatest(SET_SEARCH_PARAMS_PAGE, setSearchParamsPage),
  ]);
}



// WEBPACK FOOTER //
// ./app/Serp/sagas.js